import React, { useEffect, useState } from "react";
import SimpleTableView from "@/components/common/table/SimpleTableView";
import { useOrgRoleUsers } from "@/services/role.services";
import Pagination from "@/common/Table/ItemsPerPage";
import { RoundLoader } from "@/common/ui/Loader";
import RenderColumn from "@/components/employee/RenderColumn";
import { motion } from "framer-motion";
import { Center } from "@/common/css";
import { RxCross2 } from "react-icons/rx";
import { FiSearch } from "react-icons/fi";
import { useDebounceSearch } from "@/hooks/useDebounceSearch";
import EmptyList from "@/components/common/EmptyList";

const columnsConfig = [
  {
    accessorKey: "empId",
    header: "Emp ID",
    cell: (info: any) => <span>{info.row.original.empId}</span>,
  },
  {
    accessorKey: "empName",
    header: "Emp name",
    cell: ({ row, column }: any) => <RenderColumn type={"profile"} row={row} column={column} />,
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: (info: any) => <span>{info.row.original.email}</span>,
  },
];

const AssignedUsers: React.FC<{ role: any }> = ({ role }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounceSearch(searchQuery, 300);
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20, totalPages: 0 });

  const { isLoading, data, isFetching, refetch } = useOrgRoleUsers(role.roleId, {
    query: debouncedQuery,
    page: pagination.pageNumber,
    perPage: pagination.pageSize,
  });

  useEffect(() => {
    refetch();
  }, [debouncedQuery, pagination.pageNumber, pagination.pageSize, refetch]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, pageNumber: page }));
  };

  const handlePerPageChange = (size: any) => {
    setPagination((prev) => ({ ...prev, pageNumber: 1, pageSize: size.value }));
  };

  useEffect(() => {
    const totalRecords = data?.pagination?.total || 0;
    const totalPages = data?.pagination?.total_pages || Math.ceil(totalRecords / pagination.pageSize);
    if (totalPages) {
      setPagination((prev) => ({ ...prev, totalPages }));
    }
    const element = document.getElementById("modal-title");
    if (element) {
      if (totalRecords) {
        element.innerHTML = `${role.roleName} (${totalRecords})`;
      }
    }
  }, [data?.pagination, role]);

  return (
    <div className="p-3 h-[calc(100%-64px)] flex flex-col">
      <div className="relative m-1 md:w-[300px] mb-5">
        <input
          type="text"
          placeholder="Search"
          className="pl-3 pr-[54px] py-1.5 w-full rounded-md border border-gray-300 text-13 !leading-normal outline-none focus:ring-1 focus:ring-text-primary placeholder:text-gray-400"
          value={searchQuery}
          onChange={(e) => {
            const value = e.target.value;
            // Block '%' and length > 50
            if (value.includes("%") || value.length > 50) return;
            // Block initial space
            if (value.length > 0 && value.trim().length === 0) return;
            setSearchQuery(value);
          }}
        />
        <Center className="absolute right-3 top-1/2 -translate-y-1/2">
          {searchQuery?.length > 0 ? (
            <RxCross2 className="size-5 text-text-default hover:cursor-pointer" onClick={() => setSearchQuery("")} />
          ) : (
            <FiSearch className=" text-text-primary" />
          )}
        </Center>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto relative">
          {isLoading ? (
            <motion.div
              className="absolute inset-0 bg-white/70 backdrop-blur flex items-center justify-center z-10 rounded-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <RoundLoader />
            </motion.div>
          ) : data?.users?.length === 0 ? (
            <EmptyList
              text={searchQuery ? `No records found for "${searchQuery}"` : "Oops! You don't have any assigned users"}
              icon="/icons/empty-list-file.svg"
              className=" h-full gap-y-4"
            ></EmptyList>
          ) : (
            <div className="flex-1 min-h-0 overflow-hidden">
              <SimpleTableView
                data={data?.users}
                columnsConfig={columnsConfig}
                hidePagination={true}
                tableClassName="w-full"
                columnClassName="max-w-[200px] w-[200px] min-w-[150px]"
                isLoading={isFetching}
                externalPagination={{
                  currentPage: pagination.pageNumber,
                  totalPages: pagination.totalPages,
                  onPageChange: handlePageChange,
                  perPage: { label: `Records per page ${pagination.pageSize}`, value: pagination.pageSize },
                }}
              />
            </div>
          )}
        </div>
        {pagination.totalPages > 0 && !(data?.users?.length === 0 && searchQuery.length > 0) && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white">
            <Pagination
              currentPage={pagination.pageNumber}
              totalPages={pagination.totalPages}
              perPage={{ label: `Records per page ${pagination.pageSize}`, value: pagination.pageSize }}
              perPageOptions={[10, 20, 50, 100]}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignedUsers;
